import { makeObservable, observable, runInAction } from 'mobx';
import { BaseStore } from './baseStore';
import { RootStore } from './rootStore';
import { UserStore } from './userStore';
import { LoanService } from '/src/api';
import { Loan, Note, TLoanId } from '/src/lib/models';
import {
  CreateLoanForm,
  LoanStatus,
  LoanType,
  MenuItemType,
  PaymentMethod,
  TCreateLoanRequest,
  TListLoansRequest,
  UserRole,
} from '/src/lib/types';

export interface LoanStoreProps {
  /**
   * List of Loans
   */
  loans?: Loan[];
  /**
   * Currently selected Loan
   */
  currentLoan?: Loan;
}

export class LoanStore extends BaseStore implements LoanStoreProps {
  private didInitMenu = false;
  userStore: UserStore;

  loans?: Loan[];
  currentLoan?: Loan;

  constructor(rootStore: RootStore) {
    super(rootStore);

    this.userStore = rootStore.userStore;

    makeObservable(this, {
      loans: observable,
      currentLoan: observable,
    });
  }

  /**
   * Adds Loan nav menu items to the app header.
   */
  initMenuItems = () => {
    if (this.didInitMenu || !this.rootStore.userStore.user) return;

    // Add menu items
    this.rootStore.menuStore.addMenuItem(
      'topbar',
      'Deals',
      'loans',
      !this.rootStore.userStore.isAdmin ? MenuItemType.Item : MenuItemType.Dropdown,
      '/deals(/create)?',
      false,
      undefined,
      5
    );
    if (this.rootStore.userStore.isAdmin) {
      this.rootStore.menuStore.addSubMenuItem('topbar', 'loans', 'List Deals', 'deals');
      this.rootStore.menuStore.addSubMenuItem('topbar', 'loans', 'New Deal', 'deals/create', 'deals/create', false, [
        UserRole.Admin,
      ]);
    }

    runInAction(() => (this.didInitMenu = true));
  };

  /**** Data Processing ****/

  /**
   * Formats List Loans responses.
   *
   * @param loans - An array of Loans to format
   * @returns The formatted Loans
   */
  formatLoanList = (loans: Loan[]) => {
    return loans.map((loan) => {
      // Define
      if (!loan.isOwned) loan.isOwned = false;
      if (!loan.adminFee) loan.adminFee = 0;
      if (!loan.discountFee) loan.discountFee = 0;

      // Parse
      loan.maturityDate = new Date(loan.maturityDate);
      loan.lastAdvanceDate = new Date(loan.lastAdvanceDate);
      loan.facilityFeeDate = new Date(loan.facilityFeeDate);
      loan.created = new Date(loan.created);

      // Combine
      if (!loan.notes) {
        const notes: Note[] = [];
        if (loan.socketComments && this.userStore.isAdmin) {
          loan.socketComments.forEach((comment) => {
            notes.push(comment);
          });
        }
        if (loan.companyComments) {
          loan.companyComments.forEach((comment) => {
            notes.push(comment);
          });
        }

        notes.sort((a, b) =>
          a.created && b.created ? (a.created > b.created ? 1 : b.created > a.created ? -1 : 0) : 0
        );
        loan.notes = notes;
      }

      // Return
      return loan;
    });
  };

  /**
   * Format Get Loan responses.
   *
   * @param loan - The Loan to format
   * @returns The formatted Loan
   */
  formatLoan = (loan?: Loan) => {
    if (!loan) return;

    // Define
    if (!loan.isOwned) loan.isOwned = false;
    if (!loan.adminFee) loan.adminFee = 0;
    if (!loan.discountFee) loan.discountFee = 0;

    // Parse
    loan.created = new Date(loan.created);
    loan.maturityDate = new Date(loan.maturityDate);
    loan.lastAdvanceDate = new Date(loan.lastAdvanceDate);
    loan.facilityFeeDate = new Date(loan.facilityFeeDate);

    // Return
    return loan;
  };

  /**** Loan CRUD ****/

  /**
   * Fetches a list of Loans.
   *
   * @param query - Optional query to send with the API request
   * @returns An array of Loans
   */
  query = async (query?: TListLoansRequest) => {
    // if (query) query.px = true;
    const response = await LoanService.query(query);

    runInAction(() => (this.loans = this.formatLoanList(response)));

    return this.loans;
  };

  /**
   * Fetches a single Loan and sets it as the current Loan.
   *
   * @param loanId - ID of the Loan to fetch
   * @returns A Loan object
   */
  get = async (loanId: TLoanId) => {
    const response = await LoanService.get(loanId);

    runInAction(() => (this.currentLoan = this.formatLoan(response)));

    return this.currentLoan;
  };

  /**
   * Creates an Loan.
   *
   * @param formValues - A populated CreateLoanForm object
   * @returns The new Loan
   */
  create = async (formValues: CreateLoanForm) => {
    if (!this.userStore.user) throw new Error('Not authorized');

    // Create new Loan object
    const loan: TCreateLoanRequest = {
      name: formValues.name,
      borrower: formValues.borrower,
      isOwned: formValues.dealType === LoanType.Owned,
      amount: formValues.amount, // total facility dollar amount
      interestRate: formValues.interestRate, // interest rate - store as decimal
      advanceRate: formValues.advanceRate, // interest rate - store as decimal
      originationFee: formValues.originationFee, // percentage of advance - store as decimal
      term: formValues.term, // in months
      maturityDate: formValues.maturityDate,
      lastAdvanceDate: formValues.lastAdvanceDate,
      minPmtMethod: formValues.minPmtMethod as PaymentMethod,
      minimumPayment: formValues.minimumPayment, // dollar amount
      payDownPercent: formValues.payDownPercent,
      status: LoanStatus.Active,
      socketComments: formValues.socketComments,
      companyComments: formValues.companyComments,
      files: formValues.files,
      feeBalance: 0,
      adminFee: formValues.adminFee,
      discountFee: formValues.discountFee,
      interestBalance: 0,
      principalBalance: 0,
      principalDateUpdated: formValues.principalDateUpdated,
      assetValue: 0,
      lenders: formValues.lenders,
    };

    const response = await LoanService.create(loan);

    return response;
  };

  /**
   * Updates an Loan.
   *
   * @param loan - The Loan to update
   * @returns The updated Loan
   */
  update = async (loan: Loan) => {
    const response = await LoanService.update(loan._id, loan);

    runInAction(() => (this.currentLoan = Object.assign(this.currentLoan ?? {}, response)));

    return this.currentLoan;
  };

  /**
   * Deactivates a Loan. This is equivalent to a soft delete.
   *
   * @param loanId - ID of the Loan to deactivate
   * @returns The deactivated Loan
   */
  deactivate = async (loanId: TLoanId) => {
    if (!loanId) return;

    return await LoanService.remove(loanId);
  };
}
